import { createFileRoute, redirect } from "@tanstack/react-router";
import { AuthShell } from "@/components/auth/auth-shell";
import { ResetPasswordForm } from "@/components/reset-password-form";
import { useRedirectIfAuthenticated } from "@/lib/auth/hooks";
import { buildLinks, buildMetaTags, getCanonical } from "@/lib/seo/meta";
import { useAuthStore } from "@/stores/auth-store";

type ResetPasswordSearch = {
	token?: string;
};

export const Route = createFileRoute("/reset-password")({
	// Redirect authenticated users to dashboard
	// Auth checks are client-side only (no redirects during SSR)
	beforeLoad: ({ search }) => {
		// Skip auth checks during SSR - we have no localStorage on the server
		// The client will handle redirects after hydration
		if (typeof window === "undefined") {
			return;
		}

		const { isAuthenticated } = useAuthStore.getState();

		if (isAuthenticated) {
			// Redirect to the saved redirect path or dashboard
			const redirectTo = (search as { redirect?: string })?.redirect || "/";
			throw redirect({ to: redirectTo });
		}
	},

	head: () => {
		const meta = buildMetaTags({
			title: "Reset Password",
			description: "Set a new password for your m8tes.ai account",
			noIndex: true,
			noFollow: true,
		});

		const links = buildLinks({
			canonical: getCanonical("/reset-password"),
		});

		return { meta, links };
	},

	component: ResetPasswordPage,

	validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => ({
		token: search.token as string | undefined,
	}),
});

function ResetPasswordPage() {
	// Redirect if already authenticated (client-side check for immediate feedback)
	useRedirectIfAuthenticated("/");

	const { token } = Route.useSearch();

	return (
		<AuthShell>
			<ResetPasswordForm token={token} />
		</AuthShell>
	);
}
